/**
 * Runtime config loaded from /config.json, which prepare-config generates from the deployment
 * manifest in both dev and production. `configSchema` validates and normalizes it per auth mode.
 */

import { z } from "zod"

/**
 * Accept an array or a comma-separated string (config.json's multi-value form) as `string[]`.
 * Reject an empty string so a blank required field fails instead of becoming `[]`.
 */
const csvList = z.union([
  z.array(z.string()),
  z
    .string()
    .min(1)
    .transform((s) => s.split(",").filter(Boolean)),
])

/**
 * Treat config.json's empty-string placeholder (a CfnParameter's default for an unused field) as
 * absent. Any other value must still match `schema`.
 */
function emptyToUndefined<T extends z.ZodType>(schema: T) {
  return z.preprocess((v) => (v === "" ? undefined : v), schema.optional())
}

const baseFields = {
  cognitoUserPoolId: z.string().min(1),
  cognitoClientId: z.string().min(1),
  awsRegion: z.string().min(1),
}

const nativeSchema = z.object({
  ...baseFields,
  authMode: z.literal("native"),
})

const federatedSchema = z.object({
  ...baseFields,
  authMode: z.literal("federated"),
  cognitoDomain: z.string().min(1),
  idpProviderName: z.string().min(1),
  redirectSignIn: z.string().min(1),
  redirectSignOut: z.string().min(1),
  scopes: csvList,
  // Used only at logout: Cognito does not end the federated IDP session, so the app finds the
  // IDP's end-session endpoint from this issuer. Set only for OIDC federation; SAML leaves them
  // empty and logout falls back to /login.
  oidcIssuerUrl: emptyToUndefined(z.url()),
  oidcClientId: emptyToUndefined(z.string().min(1)),
})

/**
 * Direct-OIDC config: the frontend is an OIDC Relying Party against the customer IDP, and maps the
 * IDP token's claims to CDR's user shape.
 */
const directOidcSchema = z.object({
  ...baseFields,
  authMode: z.literal("direct-oidc"),
  oidcIssuerUrl: z.string().min(1),
  oidcClientId: z.string().min(1),
  oidcScopes: csvList,
  oidcRedirectUri: z.string().min(1),
  oidcPostLogoutRedirectUri: z.string().min(1),
  claimIdentifier: z.string().min(1),
  claimEmail: csvList,
})

const configSchema = z.discriminatedUnion("authMode", [
  nativeSchema,
  federatedSchema,
  directOidcSchema,
])

/**
 * Validated app config. Narrow on `authMode` to read mode-specific fields.
 */
export type AppConfig = z.infer<typeof configSchema>
export type FederatedConfig = z.infer<typeof federatedSchema>
export type DirectOidcConfig = z.infer<typeof directOidcSchema>

let _config: AppConfig | null = null

/**
 * Validate raw config.json into an `AppConfig`, defaulting `authMode` to "native". Throw a message
 * listing every problem on failure.
 */
export function parseConfig(raw: unknown): AppConfig {
  const obj = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}
  const result = configSchema.safeParse({ ...obj, authMode: obj.authMode ?? "native" })
  if (!result.success) {
    throw new Error(`Invalid config: ${z.prettifyError(result.error)}`)
  }
  return result.data
}

export async function loadConfig(): Promise<AppConfig> {
  if (_config) return _config

  // Tests mock auth, so dummy values are enough.
  if (import.meta.env.MODE === "test") {
    _config = parseConfig({
      cognitoUserPoolId: "test-pool-id",
      cognitoClientId: "test-client-id",
      awsRegion: "us-east-1",
      authMode: "native",
    })
    return _config
  }

  // `pnpm run dev` writes /config.json to public/; in production CdrFrontendStack writes it to S3.
  const response = await fetch("/config.json", { signal: AbortSignal.timeout(10_000) })
  if (!response.ok) {
    throw new Error(`Failed to load config: ${response.status}`)
  }
  _config = parseConfig(await response.json())
  return _config
}

export function getConfig(): AppConfig {
  if (!_config) throw new Error("Config not loaded - call loadConfig() first")
  return _config
}

/**
 * Reset config so a test can reload it with different values.
 */
export function resetConfig(): void {
  _config = null
}

/**
 * Override config in tests. Use after resetConfig() to switch modes.
 */
export function setTestConfig(config: AppConfig): void {
  _config = config
}

/**
 * Return the loaded config as its federated variant. Throw in any other mode so misuse fails at the
 * call site.
 */
export function getFederatedConfig(): FederatedConfig {
  const config = getConfig()
  if (config.authMode !== "federated") {
    throw new Error(
      `Federated config accessed but authMode is "${config.authMode}", not "federated".`,
    )
  }
  return config
}

/**
 * Return the loaded config as its direct-oidc variant. Throw in any other mode.
 */
export function getDirectOidcConfig(): DirectOidcConfig {
  const config = getConfig()
  if (config.authMode !== "direct-oidc") {
    throw new Error(
      `Direct-OIDC config accessed but authMode is "${config.authMode}", not "direct-oidc".`,
    )
  }
  return config
}
